<script setup lang="ts">
import {onMounted, onUnmounted} from "vue";
import {ArrowLeftRight, Check, Plus, Repeat, X} from "@lucide/vue";
import {CounsellingPlanLine} from "@/medication-checkup/interfaces/counselling-plan";
import {InterventionStatus} from "@/medication-checkup/interfaces/interventions";

/**
 * The posology plan shown to the patient, full screen in the main window:
 * big type, one card per medication, what changes first.
 *
 * It renders the lines it is handed, posologies and advice resolved — the
 * scene's drafts while live, the record once validated — so the pharmacist
 * can show the plan as it is being entered.
 */

defineProps<{
    lines: Array<{line: CounsellingPlanLine; posology: string | null; advice: string | null}>;
    patient: string;
    date: string;
}>();

const emit = defineEmits<{close: []}>();

/**
 * How each status reads for the patient: the card's tint, its icon and its
 * badge. What does not change wears no badge.
 */
const LOOKS: Record<InterventionStatus, {card: string; disc: string; icon: unknown; badge: string | null; tone: string}> = {
    stopped: {card: 'border-red-light/40 bg-red-light/5', disc: 'bg-red-light/15 text-red-light', icon: X, badge: 'Arrêté', tone: 'bg-red-light'},
    posology_modified: {card: 'border-orange bg-orange-light/20', disc: 'bg-orange-light/60 text-gray-dark', icon: Repeat, badge: 'Modifié', tone: 'bg-gray-dark'},
    replaced: {card: 'border-lightPurple bg-lightPurple-lightest', disc: 'bg-lightPurple text-primary', icon: ArrowLeftRight, badge: 'Remplacé', tone: 'bg-primary'},
    added: {card: 'border-secondary bg-secondary-lightest/40', disc: 'bg-secondary-lightest text-secondary-darkest', icon: Plus, badge: 'Nouveau', tone: 'bg-secondary-darkest'},
    unchanged: {card: 'border-gray-light bg-white', disc: 'bg-secondary-lightest text-secondary-darkest', icon: Check, badge: null, tone: ''},
};

const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        emit('close');
    }
};

onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[2000] flex flex-col bg-white text-gray-dark" role="dialog" aria-modal="true" aria-labelledby="medication-checkup-plan-fullscreen-title">
      <div class="flex items-center gap-4 px-9 py-5 bg-gradient-to-r from-primary-dark to-primary">
        <span class="w-11 h-11 rounded-full bg-secondary shrink-0" aria-hidden="true" />
        <div class="flex flex-col flex-1 min-w-0">
          <h1 id="medication-checkup-plan-fullscreen-title" class="m-0 font-['Comfortaa'] text-[28px] font-bold !text-white leading-tight">
            Votre nouveau plan de médicaments
          </h1>
          <span class="text-base text-apodisPink">{{ patient }} · mis à jour le {{ date }}</span>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border-[1.5px] border-solid border-white/80 px-7 py-3.5 text-[17px] font-semibold !text-white"
          @click="emit('close')"
        >
          <X class="w-[18px] h-[18px]" />
          Fermer
        </button>
      </div>

      <div class="flex-1 overflow-y-auto py-8">
        <div class="mx-auto flex w-full max-w-[1056px] flex-col gap-4 px-6">
          <div
            v-for="{line, posology, advice} in lines"
            :key="`${line.intervention_id}-${line.doctor_decision_id}`"
            class="flex items-center gap-6 rounded-2xl border-2 border-solid px-7 py-5"
            :class="LOOKS[line.status].card"
          >
            <span class="flex w-14 h-14 shrink-0 items-center justify-center rounded-full" :class="LOOKS[line.status].disc">
              <component :is="LOOKS[line.status].icon" class="w-6 h-6" />
            </span>
            <div class="flex flex-1 flex-col gap-1 min-w-0">
              <span class="text-[28px] font-bold leading-tight" :class="{'line-through': line.status === 'stopped'}">
                {{ line.replacement ?? line.label }}
              </span>
              <span v-if="line.status === 'stopped'" class="text-xl font-semibold text-red-light">À ne plus prendre</span>
              <span v-else-if="posology" class="text-xl font-semibold">{{ posology }}</span>
              <span v-if="line.replacement" class="text-lg text-muted-foreground">à la place de {{ line.label }}</span>
              <span v-if="advice" class="text-lg text-muted-foreground">{{ advice }}</span>
            </div>
            <span
              v-if="LOOKS[line.status].badge"
              class="shrink-0 rounded-full px-5 py-2.5 text-base font-bold uppercase !text-white"
              :class="LOOKS[line.status].tone"
            >{{ LOOKS[line.status].badge }}</span>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
